import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Radio, Zap, FastForward, Brain, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { trackPrediction } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import OverPredictionForm from "../components/prediction/OverPredictionForm";
import ClaudeAIPredictionCard from "../components/prediction/ClaudeAIPredictionCard";
import PredictionsPanel from "../components/prediction/PredictionsPanel";
import BallByBallFeed from "../components/match/BallByBallFeed";
import LiveOverTimeline from "../components/match/LiveOverTimeline";

export default function MatchDetailPage() {
  const { id } = useParams();
  const { user, setAuthOpen, refresh } = useAuth();
  const [match, setMatch] = useState(null);
  const [ballLog, setBallLog] = useState([]);
  const [simulating, setSimulating] = useState(false);
  const [myPrediction, setMyPrediction] = useState(null);
  const [aiForecast, setAiForecast] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const loadMatch = useCallback(async () => {
    const r = await api.get(`/matches/${id}`);
    setMatch(r.data);
  }, [id]);

  const loadMine = useCallback(async () => {
    if (!user) { setMyPrediction(null); return; }
    try {
      const r = await api.get(`/matches/${id}/my-prediction`);
      setMyPrediction(r.data);
    } catch (_) { setMyPrediction(null); }
  }, [id, user]);

  useEffect(() => { loadMatch(); }, [loadMatch]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const fetchAiForecast = useCallback(async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/matches/${id}/ai-forecast`);
      setAiForecast(r.data);
    } catch (e) {
      toast.error("Oracle AI could not be reached");
    } finally {
      setAiLoading(false);
    }
  }, [id]);

  const simulateBall = async () => {
    if (simulating) return;
    setSimulating(true);
    try {
      const r = await api.post(`/matches/${id}/simulate-ball`);
      const { ball, over_completed, over_runs_total, wicket_in_over, finished_over_number, match: m2 } = r.data;
      setBallLog((b) => [{ ...ball, ts: Date.now() }, ...b].slice(0, 60));
      setMatch(m2);
      if (over_completed) {
        toast.success(`Over #${finished_over_number} complete → ${over_runs_total} runs${wicket_in_over ? " + Wicket" : ""}`);
        await loadMine();
        if (user) { try { await refresh(); } catch (_) {} }
        // clear AI forecast so user can request a fresh one
        setAiForecast(null);
      }
    } finally {
      setSimulating(false);
    }
  };

  const simulateFullOver = async () => {
    if (simulating) return;
    setSimulating(true);
    try {
      for (let i = 0; i < 8; i++) {
        // simulate up to 8 balls (accounting for extras); break when over ends
        const r = await api.post(`/matches/${id}/simulate-ball`);
        const { ball, over_completed, over_runs_total, wicket_in_over, finished_over_number, match: m2 } = r.data;
        setBallLog((b) => [{ ...ball, ts: Date.now() + i }, ...b].slice(0, 60));
        setMatch(m2);
        if (over_completed) {
          toast.success(`Over #${finished_over_number} complete → ${over_runs_total} runs${wicket_in_over ? " + Wicket" : ""}`);
          await loadMine();
          if (user) { try { await refresh(); } catch (_) {} }
          setAiForecast(null);
          break;
        }
      }
    } finally {
      setSimulating(false);
    }
  };

  if (!match) return <div className="p-12 text-slate-400">Loading match…</div>;

  const upcomingOver = match.overs_completed + 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6" data-testid="back-to-matches">
        <ArrowLeft className="w-4 h-4" /> Back to matches
      </Link>

      {/* Scoreboard hero */}
      <div className="tile p-6 lg:p-8 grain mb-6 relative overflow-hidden">
        <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {match.status === "LIVE" ? (
              <div className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"><span className="live-dot" />LIVE</div>
            ) : (
              <div className="pill bg-sky-500/15 text-sky-300 border border-sky-500/30">{match.status}</div>
            )}
            <div className="pill bg-amber-500/15 text-amber-300 border border-amber-500/30">{match.format}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5"><MapPin className="w-3 h-3" />{match.venue}</div>
          </div>

          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
            {match.title}
          </h1>
          <div className="text-slate-400 text-sm mt-2">{match.series}</div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div>
              <div className="section-label">{match.batting_team} • Innings {match.innings_no}</div>
              <div className="metric-value text-5xl text-emerald-400 mt-1" data-testid="match-score">{match.score}/{match.wickets}</div>
              <div className="font-mono-data text-slate-400 text-sm mt-1">Overs {match.overs_completed}.{match.balls_this_over}{match.target ? ` • Target ${match.target}` : ""}</div>
            </div>
            <div>
              <div className="section-label">Striker</div>
              <div className="mt-1 font-display font-bold text-xl">{match.striker?.name}</div>
              <div className="font-mono-data text-emerald-400">{match.striker?.runs}<span className="text-slate-500">({match.striker?.balls})</span></div>
              <div className="section-label mt-3">Non-striker</div>
              <div className="mt-1 font-mono-data text-sm text-slate-300">{match.non_striker?.name} • {match.non_striker?.runs}({match.non_striker?.balls})</div>
            </div>
            <div>
              <div className="section-label">Bowler</div>
              <div className="mt-1 font-display font-bold text-xl">{match.bowler?.name}</div>
              <div className="font-mono-data text-slate-300 text-sm">{match.bowler?.overs} - {match.bowler?.runs} - {match.bowler?.wickets}W</div>
              <div className="section-label mt-3">Recent overs</div>
              <div className="flex gap-1 mt-1">
                {(match.recent_overs || []).slice(-6).map((r, i) => (
                  <span key={i} className="ball-chip bg-[#0B0F17]" style={{ minWidth: "2.25rem" }}>{r}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main bento */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-6">
          <LiveOverTimeline match={match} />
          <PredictionsPanel matchId={match.id} overNumber={upcomingOver} />
          <BallByBallFeed ballLog={ballLog} />
          <ClaudeAIPredictionCard forecast={aiForecast} loading={aiLoading} onFetch={fetchAiForecast} upcomingOver={upcomingOver} />
        </div>

        <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
          {/* Simulator */}
          <div className="tile p-6 grain">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-4 h-4 text-emerald-400" />
              <div className="section-label">Live simulator</div>
            </div>
            <p className="text-sm text-slate-400 mb-4">Bowl the next delivery or fast-forward to over end to see how your prediction resolves.</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={simulateBall} disabled={simulating} className="btn-ghost" data-testid="simulate-next-ball-button">
                <Zap className="w-4 h-4 inline mr-1" /> Next ball
              </button>
              <button onClick={simulateFullOver} disabled={simulating} className="btn-primary" data-testid="simulate-over-button">
                <FastForward className="w-4 h-4 inline mr-1" /> Finish over
              </button>
            </div>
          </div>

          {/* Prediction form */}
          {user ? (
            <OverPredictionForm
              match={match}
              upcomingOver={upcomingOver}
              existing={myPrediction}
              onSubmit={async (payload) => {
                try {
                  await api.post("/predictions", payload);
                  trackPrediction({ match_type: "demo", market: "over_runs_wicket", over: upcomingOver });
                  toast.success(`Locked in for over #${upcomingOver}`);
                  await loadMine();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Prediction failed");
                }
              }}
            />
          ) : (
            <div className="tile p-6 text-center">
              <Sparkles className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <div className="font-display font-bold uppercase">Sign in to predict</div>
              <div className="text-slate-400 text-sm mt-1 mb-4">Log your call and earn points.</div>
              <button className="btn-primary w-full" onClick={() => setAuthOpen(true)} data-testid="match-signin-cta">Enter arena</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
